import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, of } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { PayeeService } from './payee.service';
import { DbService } from './db.service';
import { Payee } from '../../models/external-system.model';
import { environment } from 'src/environments/environment';
import { Timestamp } from '@angular/fire/firestore';
import { switchMap } from 'rxjs/operators';
import { StorageService } from './storage.service';

declare let docuSignClick: any;

@Injectable({
  providedIn: 'root'
})
export class ContractService {
  private auth = inject(AuthService);
  private payeeService = inject(PayeeService);
  private db = inject(DbService);
  private storageService = inject(StorageService);

  private contractAgreementRequiredSubject = new BehaviorSubject<boolean>(false);
  private agreementVerifiedSubject = new BehaviorSubject<boolean>(false);
  private agreementUpdateRequiredSubject = new BehaviorSubject<boolean>(false);
  private showSpinnerSubject = new BehaviorSubject<boolean>(false);

  contractAgreementRequired$ = this.contractAgreementRequiredSubject.asObservable();
  agreementVerified$ = this.agreementVerifiedSubject.asObservable();
  agreementUpdateRequired$ = this.agreementUpdateRequiredSubject.asObservable();
  showSpinner$ = this.showSpinnerSubject.asObservable();

  constructor() {
    this.observeAuthState();
  }

  observeAuthState() {
    return this.auth.loggedIn$.subscribe(loggedIn => {
      if (loggedIn === false) {
        this.clearContract();
      } 
      if (loggedIn === true) {
        this.agreementVerifiedSubject.next(false);
      }
    });
  }

  initializeContractCheck() {
    return combineLatest([
      this.auth.customClaims$,
      this.payeeService.signedInPayee$
    ]).pipe(
      switchMap(([customClaims, payee]) => {
        if (!payee?.internalProperties?.parentPayee) {
          return combineLatest([of(customClaims), of(payee)]);
        }
        return combineLatest([
          of(customClaims),
          this.db.getPayee(payee.internalProperties.parentPayee)
        ]);
      })
    ).subscribe(([customClaims, payee]) => {

      if (!customClaims || !payee || this.agreementVerifiedSubject.value) {
        return;
      }

      this.agreementUpdateRequiredSubject.next(false);

      const hasContractAgreement =
        !this.auth.hasAnyCustomClaims(customClaims, ['manager', 'finance'])
        && payee?.internalProperties?.contract?.clickId;

      if (hasContractAgreement) {
        this.displayContractIfNeeded(payee);
        this.contractAgreementRequiredSubject.next(
          Boolean(payee.internalProperties?.contract?.clickId
          && !payee.internalProperties?.contract?.agreementId)
        );
        if (!this.contractAgreementRequiredSubject.value) {
          this.showSpinnerSubject.next(false);
        } else {
          this.showSpinnerSubject.next(true);
        }
      } else {
        this.contractAgreementRequiredSubject.next(false);
        this.agreementUpdateRequiredSubject.next(false);
        this.showSpinnerSubject.next(false);
      }
    });
  }

  private displayContractIfNeeded(payee: Payee) {
    docuSignClick.Clickwrap.render({
      environment: environment.docusign.environment,
      accountId: environment.docusign.accountId,
      clickwrapId: payee.internalProperties?.contract?.clickId,
      clientUserId: payee.id,
      locale: 'pt',
      documentData: {
        fullName: payee.companyName,
        email: payee.internalProperties?.email || '',
        taxId: payee.internalProperties?.taxId || '',
        date: new Date().toLocaleDateString('pt-BR'),
      },
      style: {
        scrollControl: 'fill_parent',
        container: {
          backgroundColor: '#e7ebef',
          borderWidth: '1px',
        },
        agreeButton: {
          height: '40px',
          width: '100%',
          margin: '10px 0',
          padding: '3px 0',
          fontSize: '16px',
          fontWeight: 'bold',
          backgroundColor: '#ff7700',
        }
      },
      onMustAgree: (agreementData: any) => {
        this.showSpinnerSubject.next(false);
        this.contractAgreementRequiredSubject.next(true);
        if (payee.internalProperties?.contract?.hasSignedBefore) {
          this.agreementUpdateRequiredSubject.next(true);
        }
        if (payee.internalProperties?.contract?.clickId !== agreementData.clickwrapId) {
          this.db.updateContract(payee.id, {
            'internalProperties.contract.clickId': agreementData.clickwrapId,
            'internalProperties.contract.agreementId': null,
          });
        }
      },
      onAgreed: (agreementData: any) => {
        this.showSpinnerSubject.next(false);
        this.contractAgreementRequiredSubject.next(false);
        this.agreementUpdateRequiredSubject.next(false);

        if (payee.internalProperties?.contract?.clickId !== agreementData.clickwrapId
          || payee.internalProperties?.contract?.agreementId !== agreementData.agreementId
          || payee.internalProperties?.contract?.hasSignedBefore !== true
        ) {
          this.db.updateContract(payee.id, {
            'internalProperties.contract.clickId': agreementData.clickwrapId,
            'internalProperties.contract.agreementId': agreementData.agreementId,
            'internalProperties.contract.hasSignedBefore': true,
            'internalProperties.contract.signedDate': payee.internalProperties?.contract?.signedDate || Timestamp.now(),
          });
        }
      }
    }, '#ds-clickwrap');
  }

  setShowSpinner(value: boolean) {
    this.showSpinnerSubject.next(value);
  }

  clearContract() {
    this.contractAgreementRequiredSubject.next(false);
    this.agreementVerifiedSubject.next(true);
    this.agreementUpdateRequiredSubject.next(false);
    this.showSpinnerSubject.next(false);
  }

  async downloadContract() {
    // TODO - authenticate with docusign
    let payee = await firstValueFrom(this.payeeService.signedInPayee$);
    
    if (payee?.internalProperties?.parentPayee) {
      payee = await firstValueFrom(this.db.getPayee(payee.internalProperties.parentPayee));
    }
    
    const accountId = environment.docusign.accountId;
    const clickwrapId = payee.internalProperties?.contract?.clickId;
    const agreementId = payee.internalProperties?.contract?.agreementId;
    const downloadUrl = `${environment.docusign.environment}/clickapi/v1/accounts/${accountId}/clickwraps/${clickwrapId}/agreements/${agreementId}/download?include_coc=true`;

    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    this.storageService.downloadBlob(blob, `Contrato -${payee.companyName}.pdf`);

  }
} 